// @credit: 76a97b1cd4349534e19fb23cf80c4a0f1b1502418015de1e9d68b95c44bc36d433ed49b8
//
// Aphilaty PWA — Add Offer Page
//
// New / Current / Historical lifecycle tabs.
// Requested Items / Unrequested Items flavor tabs.
// Offer submission goes through submitContent Cloud Function.
// Excessive discount guard: warn at ≥90%, hold at ≥90% for platform review.

import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import {
  subscribeToDoc, subscribeToQuery, callFunction,
  where, orderBy, limit, AppError,
} from '../../services/firebase';

interface OfferListItem {
  id: string;
  orgId: string;
  subject: string;
  body?: string;
  items: { desc: string }[];
  status: 'live' | 'revoked' | 'expired';
  publishedAt: any;
  expiresAt?: any;
  isDeleted?: boolean;
}

interface OfferSubmissionItem {
  desc: string;
  brand?: string;
  type?: string;
  currentPrice?: number;
  offerPrice?: number;
  offerType?: OfferType;
  url?: string;
}

interface SubmissionDoc {
  id: string;
  status: string;
  createdAt: any;
  contentData?: {
    subject?: string;
    body?: string;
    items?: OfferSubmissionItem[];
    flavor?: FlavorTab;
    deliveryMode?: 'immediate' | 'batch';
    isExclusiveToOrg?: boolean;
    expiresAt?: { toDate?: () => Date } | null;
    targetMemberTypes?: Array<'consumer' | 'staff'>;
  };
}

// content_submissions statuses worth surfacing to org admins — 'posted' graduates
// to the live offers list, 'blocked_csam' routes to the legal/CLO pipeline.
const submissionStatusMeta: Record<string, { label: string; badge: string }> = {
  pending_ai:         { label: 'Submitted · scanning',           badge: 'badge-gray' },
  pending_post:       { label: 'Cleared · posts within 1 hour',  badge: 'badge-blue' },
  held_flagged:       { label: 'Held for review',                badge: 'badge-amber' },
  pending_clo_review: { label: 'Held for review',                badge: 'badge-amber' },
  held_scan_error:    { label: 'Held for review',                badge: 'badge-amber' },
};

// Submissions can only be edited or withdrawn while review is still in
// progress — once a submission reaches 'pending_post' it's "Approved · queued
// for delivery" and can no longer be changed here, only revoked once live.
const isPreReview = (status: string) => status !== 'pending_post';

interface RollupItem {
  desc: string;
  brand?: string;
  type: string;
  userCount: number;
  openCount: number;
  avgUnitPrice?: number;
  hasActiveOffer?: boolean;
  activeOfferId?: string;
}

type OfferType = 'Discount' | 'BOGO' | '% Off' | '3 for 2' | 'Free Item';

interface ItemRow extends RollupItem {
  selected: boolean;
  offerType: OfferType;
  offerPrice: string;
  url: string;
}

type LifecycleTab = 'new' | 'current' | 'historical';
type FlavorTab = 'requested' | 'unrequested';

export default function AddOfferPage() {
  const { activeOrgId, activeOrg, user, isPlatformAdmin } = useAuth();
  const [lifecycle, setLifecycle] = useState<LifecycleTab>('new');
  const [flavor, setFlavor] = useState<FlavorTab>('requested');
  const [to, setTo] = useState('consumers');
  const [isExclusiveToOrg, setIsExclusiveToOrg] = useState(false);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [items, setItems] = useState<ItemRow[]>([]);
  const [unreqItems, setUnreqItems] = useState<ItemRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [showDiscountWarn, setShowDiscountWarn] = useState(false);
  const [deliveryMode, setDeliveryMode] = useState<'immediate' | 'batch'>('batch');
  const [memberCount, setMemberCount] = useState<number>(0);
  const [showLargeOrgWarn, setShowLargeOrgWarn] = useState(false);
  const [expirationMode, setExpirationMode] = useState<'date' | 'never'>('date');
  const [expiresAt, setExpiresAt] = useState('');
  const [showNeverExpiresWarn, setShowNeverExpiresWarn] = useState(false);
  const [allOffers, setAllOffers] = useState<OfferListItem[]>([]);
  const [offersLoading, setOffersLoading] = useState(true);
  const [pendingSubmissions, setPendingSubmissions] = useState<SubmissionDoc[]>([]);
  const [editingSubmissionId, setEditingSubmissionId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const today = new Date().toISOString().split('T')[0];

  // Load rollup items
  useEffect(() => {
    if (!activeOrgId) return;
    setIsLoading(true);

    const unsub = subscribeToDoc<Record<string, RollupItem>>(
      `org_list_rollups/${activeOrgId}/snapshots/${today}`,
      data => {
        if (data) {
          const rollupItems = Object.entries(data)
            .filter(([k]) => k !== 'id' && k !== 'orgId' && k !== 'date' && k !== 'generatedAt'
              && k !== 'cohortFloor' && k !== 'itemCount')
            .map(([, item]) => ({
              ...item,
              selected: false,
              offerType: 'Discount' as OfferType,
              offerPrice: item.avgUnitPrice
                ? (item.avgUnitPrice * 0.85).toFixed(2)
                : '',
              url: '',
            }))
            .sort((a, b) => b.userCount - a.userCount);
          setItems(rollupItems);
        }
        setIsLoading(false);
      },
      err => {
        console.error('[AddOffer] rollup subscription error:', err);
        setIsLoading(false);
      }
    );
    return unsub;
  }, [activeOrgId]);

  // Load org delivery mode
  useEffect(() => {
    if (!activeOrgId) return;
    import('../../services/firebase').then(({ getDocument }) => {
      getDocument<{ deliveryMode?: string; memberCount?: number }>(
        `organizations/${activeOrgId}`
      ).then(org => {
        setDeliveryMode((org?.deliveryMode as 'immediate' | 'batch') ?? 'batch');
        setMemberCount(org?.memberCount ?? 0);
      });
    });
  }, [activeOrgId]);

  // Load this org's offers — split into Current (live) / Historical (revoked, expired) tabs
  useEffect(() => {
    if (!activeOrgId || !user) return;
    setOffersLoading(true);
    const constraints = isPlatformAdmin
      ? [where('orgId', '==', activeOrgId), limit(200)]
      : [where(`memberUidMap.${user.uid}`, '==', true), limit(200)];
    return subscribeToQuery<OfferListItem>(
      'offers',
      constraints,
      data => {
        setAllOffers(
          data
            .filter(o => !o.isDeleted && (isPlatformAdmin || o.orgId === activeOrgId))
            .sort((a, b) => (b.publishedAt?.seconds ?? 0) - (a.publishedAt?.seconds ?? 0))
        );
        setOffersLoading(false);
      },
      err => { console.error('[offers subscription]', err); setOffersLoading(false); }
    );
  }, [activeOrgId, user?.uid, isPlatformAdmin]);

  // Submissions still moving through the moderation pipeline — not yet live
  useEffect(() => {
    if (!activeOrgId) return;
    return subscribeToQuery<SubmissionDoc>(
      'content_submissions',
      [where('orgId', '==', activeOrgId), where('contentType', '==', 'offer'), limit(20)],
      data => setPendingSubmissions(
        data
          .filter(s => submissionStatusMeta[s.status])
          .sort((a, b) => (b.createdAt?.seconds ?? 0) - (a.createdAt?.seconds ?? 0))
      ),
      err => console.error('[offers pending subscription]', err)
    );
  }, [activeOrgId]);

  const checkDiscounts = (rows: ItemRow[]) => {
    const hasExcessive = rows.some(row => {
      if (!row.selected || !row.offerPrice || !row.avgUnitPrice) return false;
      const off = parseFloat(row.offerPrice);
      const cur = row.avgUnitPrice;
      return cur > 0 && (cur - off) / cur >= 0.9;
    });
    setShowDiscountWarn(hasExcessive);
  };

  const toggleItem = (idx: number) => {
    const next = items.map((it, i) => i === idx ? { ...it, selected: !it.selected } : it);
    setItems(next);
    checkDiscounts(next);
  };

  const updateItem = (idx: number, field: keyof ItemRow, value: string) => {
    const next = items.map((it, i) => i === idx ? { ...it, [field]: value } : it);
    setItems(next);
    checkDiscounts(next);
  };

  const resetComposeForm = () => {
    setEditingSubmissionId(null);
    setSubject(''); setBody('');
    setItems(prev => prev.map(it => ({ ...it, selected: false })));
    setUnreqItems(prev => prev.map(it => ({ ...it, selected: false })));
    setFlavor('requested'); setTo('consumers'); setDeliveryMode('batch');
    setExpirationMode('date'); setExpiresAt(''); setShowNeverExpiresWarn(false);
    setError(null);
  };

  const handleSubmit = async () => {
    if (!subject.trim()) { setError('Subject is required.'); return; }

    const selectedItems = (flavor === 'requested' ? items : unreqItems)
      .filter(it => it.selected)
      .map(it => ({
        desc: it.desc, brand: it.brand, type: it.type,
        currentPrice: it.avgUnitPrice,
        offerPrice: parseFloat(it.offerPrice) || undefined,
        offerType: it.offerType, url: it.url || undefined,
      }));

    if (!selectedItems.length) { setError('Select at least one item.'); return; }

    if (expirationMode === 'date' && !expiresAt) {
      setError('Set an expiration date, or choose "Never expires".');
      return;
    }

    const contentData = {
      subject, body, items: selectedItems,
      flavor,
      deliveryMode,
      isExclusiveToOrg,
      expiresAt: expirationMode === 'never' ? null : new Date(expiresAt).getTime(),
      targetMemberTypes: to === 'staff' ? ['staff']
        : to === 'both' ? ['consumer', 'staff'] : ['consumer'],
    };

    setIsSubmitting(true); setError(null);
    try {
      if (editingSubmissionId) {
        await callFunction('editSubmission', { submissionId: editingSubmissionId, contentData });
        setSuccessMsg('Offer updated and resubmitted. It will post within 1 hour unless held in review.');
      } else {
        await callFunction('submitContent', {
          orgId: activeOrgId,
          contentType: 'offer',
          isUserGenerated: false,
          contentData,
        });
        setSuccessMsg('Offer submitted. It will post within 1 hour unless held in review.');
      }
      resetComposeForm();
    } catch (err) {
      setError((err as AppError).message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditSubmission = (sub: SubmissionDoc) => {
    const cd = sub.contentData;
    if (!cd) return;

    setLifecycle('new');
    setSubject(cd.subject ?? '');
    setBody(cd.body ?? '');
    setFlavor(cd.flavor ?? 'requested');
    setIsExclusiveToOrg(cd.isExclusiveToOrg ?? false);
    setTo(
      cd.targetMemberTypes?.length === 1 && cd.targetMemberTypes[0] === 'staff' ? 'staff'
      : cd.targetMemberTypes?.length === 2 ? 'both'
      : 'consumers'
    );
    setDeliveryMode(cd.deliveryMode ?? 'batch');

    const expiresAtDate = cd.expiresAt?.toDate?.() ?? null;
    if (expiresAtDate) {
      setExpirationMode('date');
      setExpiresAt(expiresAtDate.toISOString().slice(0, 16));
    } else {
      setExpirationMode('never');
      setExpiresAt('');
    }
    setShowNeverExpiresWarn(false);

    // Re-select matching rows from the current rollup by item description —
    // submitted items not present in today's rollup snapshot can't be
    // re-selected here (V1 limitation; the row model is rollup-driven).
    const submittedItems = cd.items ?? [];
    const reselect = (rows: ItemRow[]) => rows.map(row => {
      const match = submittedItems.find(it => it.desc === row.desc);
      return match
        ? { ...row, selected: true,
            offerPrice: match.offerPrice !== undefined ? String(match.offerPrice) : row.offerPrice,
            offerType: match.offerType ?? row.offerType,
            url: match.url ?? '' }
        : { ...row, selected: false };
    });
    setItems(prev => reselect(prev));
    setUnreqItems(prev => reselect(prev));

    setEditingSubmissionId(sub.id);
    setError(null); setActionError(null); setSuccessMsg(null);
  };

  const handleCancelSubmission = async (submissionId: string) => {
    setActionError(null);
    try {
      await callFunction('cancelSubmission', { submissionId });
      if (editingSubmissionId === submissionId) resetComposeForm();
    } catch (err) {
      setActionError((err as AppError).message);
    }
  };

  const offerTypes: OfferType[] = ['Discount', 'BOGO', '% Off', '3 for 2', 'Free Item'];
  const currentItems = flavor === 'requested' ? items : unreqItems;
  const lifecycleOffers = lifecycle === 'current'
    ? allOffers.filter(o => o.status === 'live')
    : allOffers.filter(o => o.status !== 'live');

  if (!activeOrgId) return (
    <div style={{ padding: 40, textAlign: 'center', color: 'var(--color-text-secondary)' }}>
      <p style={{ fontSize: 15, marginBottom: 8 }}>No org selected</p>
      <p style={{ fontSize: 13 }}>Use the org picker in the top nav to select an org, then return here to view and manage its offers.</p>
    </div>
  );

  return (
    <div>
      <h1 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>
        Offers — {activeOrg?.name}
      </h1>

      {/* Lifecycle tabs */}
      <div className="lifecycle-tabs">
        {(['new','current','historical'] as LifecycleTab[]).map(tab => (
          <button key={tab} className={`ltab${lifecycle === tab ? ' active' : ''}`}
            onClick={() => setLifecycle(tab)}>
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {lifecycle !== 'new' ? (
        <div className="card">
          {offersLoading ? (
            <div className="empty-state"><span className="spinner" /></div>
          ) : lifecycleOffers.length === 0 ? (
            <div className="empty-state">
              {lifecycle === 'current' ? 'No active offers right now.' : 'No past offers yet.'}
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Subject</th>
                  <th>Items</th>
                  <th>Status</th>
                  <th>{lifecycle === 'current' ? 'Live since' : 'Published'}</th>
                  <th>Expires</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {lifecycleOffers.map(offer => (
                  <tr key={offer.id}>
                    <td>
                      <div style={{ fontWeight: 500, fontSize: 13 }}>{offer.subject}</div>
                      {offer.body && (
                        <div style={{ fontSize: 12, color: 'var(--color-text-secondary)',
                          marginTop: 2 }}>
                          {offer.body.substring(0, 80)}{offer.body.length > 80 ? '…' : ''}
                        </div>
                      )}
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
                      {offer.items?.length ?? 0}
                    </td>
                    <td>
                      <span className={`badge ${offer.status === 'live' ? 'badge-green' : 'badge-gray'}`}>
                        {offer.status}
                      </span>
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--color-text-secondary)',
                      whiteSpace: 'nowrap' }}>
                      {offer.publishedAt?.toDate?.()?.toLocaleDateString() ?? '—'}
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--color-text-secondary)',
                      whiteSpace: 'nowrap' }}>
                      {offer.expiresAt?.toDate
                        ? offer.expiresAt.toDate().toLocaleDateString()
                        : 'Never'}
                    </td>
                    <td>
                      {offer.status === 'live' && (
                        <button className="btn btn-outline btn-sm"
                          onClick={() => callFunction('revokeContent',
                            { contentId: offer.id, contentType: 'offer', reason: 'admin_revoked' })}
                          style={{ color: 'var(--color-danger)', borderColor: 'var(--color-danger)' }}>
                          Revoke
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ) : (
        <div>
          {/* Error / success banners */}
          {error && (
            <div className="error-banner" role="alert">
              {error}
              <button onClick={() => setError(null)} style={{ marginLeft: 'auto',
                background: 'none', border: 'none', cursor: 'pointer',
                color: 'var(--color-danger)', fontSize: 16 }}>×</button>
            </div>
          )}
          {successMsg && (
            <div className="note-box" role="status" style={{ marginBottom: 16 }}>
              {successMsg}
              <button onClick={() => setSuccessMsg(null)} style={{ marginLeft: 8,
                background: 'none', border: 'none', cursor: 'pointer', fontSize: 12 }}>
                Dismiss
              </button>
            </div>
          )}
          {actionError && (
            <div className="error-banner" role="alert" style={{ marginBottom: 16 }}>
              {actionError}
              <button onClick={() => setActionError(null)} style={{ marginLeft: 'auto',
                background: 'none', border: 'none', cursor: 'pointer',
                color: 'var(--color-danger)', fontSize: 16 }}>×</button>
            </div>
          )}

          {/* Pending review — submissions still moving through moderation */}
          {pendingSubmissions.length > 0 && (
            <div className="card" style={{ marginBottom: 16 }}>
              <div className="card-header"><span className="card-title">Pending Review</span></div>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Subject / Preview</th>
                    <th>Status</th>
                    <th>Submitted</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {pendingSubmissions.map(sub => {
                    const meta = submissionStatusMeta[sub.status];
                    const cdBody = sub.contentData?.body ?? '';
                    const editable = isPreReview(sub.status);
                    return (
                      <tr key={sub.id}>
                        <td>
                          {sub.contentData?.subject && (
                            <div style={{ fontSize: 13 }}>{sub.contentData.subject}</div>
                          )}
                          <div style={{ fontSize: 12, color: 'var(--color-text-secondary)',
                            marginTop: 2 }}>
                            {cdBody.substring(0, 80)}{cdBody.length > 80 ? '…' : ''}
                          </div>
                        </td>
                        <td>
                          <span className={`badge ${meta.badge}`}>{meta.label}</span>
                        </td>
                        <td style={{ fontSize: 12, color: 'var(--color-text-secondary)',
                          whiteSpace: 'nowrap' }}>
                          {sub.createdAt?.toDate?.()?.toLocaleDateString() ?? '—'}
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          {editable ? (
                            <>
                              <button className="btn btn-outline btn-sm" style={{ marginRight: 6 }}
                                onClick={() => handleEditSubmission(sub)}>
                                Edit
                              </button>
                              <button className="btn btn-outline btn-sm"
                                onClick={() => handleCancelSubmission(sub.id)}
                                style={{ color: 'var(--color-danger)', borderColor: 'var(--color-danger)' }}>
                                Cancel
                              </button>
                            </>
                          ) : (
                            <span style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>
                              Queued — can only be revoked once live
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {editingSubmissionId && (
            <div className="note-box" style={{ marginBottom: 14 }}>
              Editing a pending submission — saving will resubmit it for a fresh review.
            </div>
          )}

          {/* To: field */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr',
            gap: 16, marginBottom: 14 }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label" htmlFor="offer-to">To:</label>
              <select id="offer-to" className="form-select" value={to}
                onChange={e => setTo(e.target.value)}>
                <option value="consumers">All affiliated members (consumers)</option>
                <option value="staff">All org staff (employees)</option>
                <option value="both">All members and staff</option>
                <option value="segment">Specific audience segment…</option>
              </select>
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label" htmlFor="offer-subject">Subject</label>
              <input id="offer-subject" type="text" className="form-input"
                value={subject} onChange={e => setSubject(e.target.value)}
                placeholder="Fresh fruit deals — this week only" />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="offer-body">Body (optional)</label>
            <textarea id="offer-body" className="form-textarea" value={body}
              onChange={e => setBody(e.target.value)} rows={2}
              placeholder="Additional details about the offer…" />
          </div>

          <div className="form-group" style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
            <input id="offer-exclusive" type="checkbox" style={{ marginTop: 3 }}
              checked={isExclusiveToOrg} onChange={e => setIsExclusiveToOrg(e.target.checked)} />
            <label htmlFor="offer-exclusive" style={{ fontSize: 13, color: 'var(--color-text)', cursor: 'pointer' }}>
              Restrict this offer to your organization only
              <span style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginTop: 2 }}>
                Prevents this offer from appearing in multi-org comparisons (future feature — no effect currently)
              </span>
            </label>
          </div>

          {/* Requested / Unrequested flavor tabs */}
          <div style={{ marginBottom: 10 }}>
            <div className="flavor-tabs">
              <button className={`ftab${flavor === 'requested' ? ' active' : ''}`}
                onClick={() => setFlavor('requested')}>Requested Items</button>
              <button className={`ftab${flavor === 'unrequested' ? ' active' : ''}`}
                onClick={() => setFlavor('unrequested')}>Unrequested Items</button>
            </div>
            <div style={{ fontSize: 11, color: 'var(--color-text-secondary)',
              marginTop: 4 }}>
              {flavor === 'requested'
                ? 'Items users have on their lists associated to this org · updated daily'
                : 'Proactive offers — from inventory or entered manually'}
            </div>
          </div>

          {/* Excessive discount warning */}
          {showDiscountWarn && (
            <div className="note-box warning" role="alert" style={{ marginBottom: 10 }}>
              ⚠ One or more items have a discount ≥90%. This offer will be held for
              Aphilaty platform review before going live.
            </div>
          )}

          {/* Items table */}
          <div className="card" style={{ marginBottom: 14 }}>
            <div style={{ overflowX: 'auto' }}>
              {isLoading ? (
                <div className="empty-state"><span className="spinner" /></div>
              ) : currentItems.length === 0 ? (
                <div className="empty-state" style={{ padding: 24 }}>
                  <p>{flavor === 'requested'
                    ? 'No requested items yet. Rollup updates daily.'
                    : 'No unrequested items. Connect inventory in Settings → Integrations (V2) or add items manually.'}</p>
                </div>
              ) : (
                <table className="data-table">
                  <thead>
                    <tr>
                      <th style={{ width: 32 }}>Select</th>
                      <th>Type</th>
                      <th>Description</th>
                      <th>URL</th>
                      <th>Brand</th>
                      <th>Offer Type</th>
                      <th>Cur. Price</th>
                      <th>Offer Price</th>
                      <th className="sorted">Users ↓</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentItems.map((item, idx) => (
                      <tr key={idx} className={item.selected ? 'has-offer' : ''}>
                        <td>
                          <input type="checkbox" checked={item.selected}
                            onChange={() => toggleItem(idx)}
                            aria-label={`Select ${item.desc}`}
                            style={{ width: 14, height: 14, accentColor: 'var(--color-primary)',
                              cursor: 'pointer' }} />
                        </td>
                        <td style={{ fontSize: 12 }}>{item.type}</td>
                        <td style={{ fontSize: 12, fontWeight: item.selected ? 500 : 400 }}>
                          {item.desc}
                        </td>
                        <td>
                          <input type="url" className="url-input" value={item.url}
                            onChange={e => updateItem(idx, 'url', e.target.value)}
                            placeholder="url…" aria-label={`URL for ${item.desc}`} />
                        </td>
                        <td style={{ fontSize: 12 }}>{item.brand ?? '—'}</td>
                        <td>
                          <select className="offer-select" value={item.offerType}
                            onChange={e => updateItem(idx, 'offerType', e.target.value)}
                            aria-label={`Offer type for ${item.desc}`}>
                            {offerTypes.map(t => <option key={t}>{t}</option>)}
                          </select>
                        </td>
                        <td style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
                          {item.avgUnitPrice ? `$${item.avgUnitPrice.toFixed(2)}` : '—'}
                        </td>
                        <td>
                          <input type="text" className={`price-input${item.selected ? ' offer' : ''}`}
                            value={item.offerPrice}
                            onChange={e => updateItem(idx, 'offerPrice', e.target.value)}
                            placeholder="$0.00" aria-label={`Offer price for ${item.desc}`} />
                        </td>
                        <td>
                          <div className="users-bar">
                            <div className="users-bar-wrap">
                              <div className="users-bar-fill"
                                style={{ width: `${Math.min(100, (item.userCount / (currentItems[0]?.userCount || 1)) * 100)}%` }} />
                            </div>
                            <span className="users-count">{item.userCount.toLocaleString()}</span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Bulk bar */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10,
              padding: '10px 14px', background: 'var(--color-bg-secondary)',
              borderTop: '0.5px solid var(--color-border)' }}>
              <span style={{ fontSize: 12, color: 'var(--color-text-secondary)', flex: 1 }}>
                {currentItems.filter(i => i.selected).length} items selected
              </span>
              <button className="btn btn-outline btn-sm" onClick={() => setError(null)}>
                Save Draft
              </button>
            </div>
          </div>

          {/* Delivery mode selector */}
          <div style={{ background: 'var(--color-bg-secondary)',
            border: '0.5px solid var(--color-border)',
            borderRadius: 'var(--radius-md)', padding: '12px 14px',
            marginBottom: 14, display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 3 }}>
                Delivery Mode
              </div>
              <div style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>
                {deliveryMode === 'batch'
                  ? 'This content will be delivered tonight at midnight ET'
                  : 'This content will be delivered immediately after review'}
              </div>
            </div>
            <div className="flavor-tabs">
              <button
                className={`ftab${deliveryMode === 'batch' ? ' active' : ''}`}
                onClick={() => { setDeliveryMode('batch'); setShowLargeOrgWarn(false); }}>
                Batch (Nightly)
              </button>
              <button
                className={`ftab${deliveryMode === 'immediate' ? ' active' : ''}`}
                onClick={() => {
                  if (memberCount >= 1000) {
                    setShowLargeOrgWarn(true);
                  } else {
                    setDeliveryMode('immediate');
                  }
                }}>
                Immediate
              </button>
            </div>
          </div>

          {/* Expiration selector */}
          <div style={{ background: 'var(--color-bg-secondary)',
            border: '0.5px solid var(--color-border)',
            borderRadius: 'var(--radius-md)', padding: '12px 14px',
            marginBottom: 14, display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 3 }}>
                Expiration
              </div>
              {expirationMode === 'date' ? (
                <input type="datetime-local" className="form-input" style={{ maxWidth: 220 }}
                  value={expiresAt} min={new Date().toISOString().slice(0, 16)}
                  onChange={e => setExpiresAt(e.target.value)}
                  aria-label="Offer expiration date and time" />
              ) : (
                <div style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>
                  This offer will stay live until you manually revoke it.
                </div>
              )}
            </div>
            <div className="flavor-tabs">
              <button
                className={`ftab${expirationMode === 'date' ? ' active' : ''}`}
                onClick={() => { setExpirationMode('date'); setShowNeverExpiresWarn(false); }}>
                Set expiration
              </button>
              <button
                className={`ftab${expirationMode === 'never' ? ' active' : ''}`}
                onClick={() => setShowNeverExpiresWarn(true)}>
                Never expires
              </button>
            </div>
          </div>

          {/* Never-expires confirmation */}
          {showNeverExpiresWarn && (
            <div className="note-box warning" style={{ marginBottom: 14 }}>
              ⚠ <strong>No expiration.</strong> This offer will remain live
              indefinitely until you manually revoke it. Are you sure?
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <button className="btn btn-outline btn-sm"
                  onClick={() => { setExpirationMode('never'); setShowNeverExpiresWarn(false); }}>
                  Yes, never expire
                </button>
                <button className="btn btn-outline btn-sm"
                  onClick={() => setShowNeverExpiresWarn(false)}>
                  Keep an expiration date
                </button>
              </div>
            </div>
          )}

          {/* Large org immediate delivery warning */}
          {showLargeOrgWarn && (
            <div className="note-box warning" style={{ marginBottom: 14 }}>
              ⚠ <strong>Large org warning.</strong> Switching to immediate will
              notify <strong>{memberCount.toLocaleString()} members</strong> as
              soon as this content passes review. Are you sure?
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <button className="btn btn-outline btn-sm"
                  onClick={() => {
                    setDeliveryMode('immediate');
                    setShowLargeOrgWarn(false);
                  }}>
                  Yes, send immediately
                </button>
                <button className="btn btn-outline btn-sm"
                  onClick={() => setShowLargeOrgWarn(false)}>
                  Keep batch delivery
                </button>
              </div>
            </div>
          )}

          {/* Action row */}
          <div style={{ display: 'flex', gap: 10, paddingTop: 14,
            borderTop: '0.5px solid var(--color-border)' }}>
            <button className="btn btn-primary" onClick={handleSubmit}
              disabled={isSubmitting}>
              {isSubmitting
                ? <span className="spinner" />
                : editingSubmissionId ? 'Save Changes' : 'Send for Review'}
            </button>
            {editingSubmissionId ? (
              <button className="btn btn-outline" onClick={resetComposeForm}>
                Cancel Edit
              </button>
            ) : (
              <>
                <button className="btn btn-outline" onClick={() => setError(null)}>
                  Save Draft
                </button>
                <button className="btn btn-outline" style={{ marginLeft: 'auto',
                  color: 'var(--color-danger)', borderColor: 'var(--color-danger)' }}>
                  Delete
                </button>
              </>
            )}
          </div>

          <div className="note-box" style={{ marginTop: 14 }}>
            Offers are reviewed by Aphilaty before delivery. If not held within 1 hour
            they send automatically. Discounts ≥90% require explicit platform review.
          </div>
        </div>
      )}
    </div>
  );
}
